import React, { useContext, useEffect, useState } from 'react';
import RouteDataLoaderContext from './RouteDataLoaderContext';

/**
 * A Higher Order Component that adds a 'routeIsLoading' prop and can optionally suspend
 * rendering or component updating while running a preloading function.
 *
 * @param {ReactNode} WrappedComponent React component to wrap
 * @param {Object} options - An options object
 * @param {boolean} options.deferUpdates - Suspends component updating until preload function has completed
 */
const withRouteDataLoader = (WrappedComponent, hocArgs) => {
    /**
     * Defers updates or rendering of WrappedComponent while route prefetch functions are running
     */
    class DataLoadingWrapper extends WrappedComponent {
        shouldComponentUpdate = nextProps => {
            const deferUpdates = hocArgs && hocArgs.deferUpdates && hocArgs.deferUpdates === true;
            const { routeIsLoading } = this.props;
            if (deferUpdates) {
                if (routeIsLoading === false && nextProps.routeIsLoading === true) {
                    return true;
                } else if (nextProps.routeIsLoading === true) {
                    return false;
                }
            }

            return true;
        };

        render() {
            /**
             * If deferring component updates, use inverse inheritance to pause
             * the wrapped component while preloading page container to redux.
             */
            return super.render();
        }
    }

    /**
     * Adds context props (routeIsLoading) from RouteDataLoader to WrappedComponent
     */
    const AddLoadingContext = props => {
        const deferUpdates = hocArgs && hocArgs.deferUpdates && hocArgs.deferUpdates === true;
        const routeIsLoading = useContext(RouteDataLoaderContext);
        const [isSSR, setIsSSR] = useState(true);
        useEffect(() => setIsSSR(false), []);

        if (!isSSR && deferUpdates && routeIsLoading) {
            return <DataLoadingWrapper {...props} routeIsLoading={routeIsLoading} />;
        }

        return <WrappedComponent {...props} routeIsLoading={routeIsLoading} />;
    };

    return AddLoadingContext;
};

export default withRouteDataLoader;
